import { Pointer } from "rfc6902";
import { getFromDataPath } from "../../../utility/getFromDataPath";
import { mutateByPath } from "../../../utility/mutateByPath";
import { AnalysisMethod } from "../../ars_ldm";
import { normalizeParameterName } from "../../utility/normalizeParameterName";
import { ArsTableBody } from "../interface";
import { isArsTableGroupCalculationDto } from "../typeguards";
import { collectParameterPaths } from "./collectParameterPaths";

/**
 * Shares operation parameters between siblings and grouping-factor parameters within an entry.
 * Operation and grouping-factor parameter values remain separate.
 * Operation parameters and target variables are shared only between sibling operations.
 * Separate top-level entries and the opposite table axis keep their own values.
 *
 * @param body - Table body containing the item being edited.
 * @param itemPath - JSON Pointer path to the edited operation or grouping factor.
 * @param inheritedMethod - Current table method resolved from ARS context in table state.
 * @param isT - Type guard for the generic table group type T.
 * @param inserting - Whether the item at itemPath was just inserted and needs values from a peer.
 * @returns An immutable body with matching parameter values across entries of the same type.
 */
export const syncParameters = <T>(
  body: ArsTableBody<T>,
  itemPath: string,
  inheritedMethod: AnalysisMethod,
  isT: (obj: unknown) => obj is T,
  inserting = false,
): ArsTableBody<T> => {
  /** Segments of the JSON Pointer identifying the edited item. */
  const pathParts = itemPath.split("/").filter(Boolean);
  /** Table axis containing the edited item. */
  const section = pathParts[0];
  /** Index of the edited item's owning table group. */
  const ownerIndex = Number(pathParts[1]);
  if (
    (section !== "columns" && section !== "rows") ||
    !Number.isInteger(ownerIndex) ||
    ownerIndex < 0 ||
    ownerIndex >= body[section].length
  ) {
    return body;
  }

  /** The operation or grouping factor addressed by the edited path. */
  const source = getFromDataPath(itemPath, body, (obj) => isArsTableGroupCalculationDto(obj, isT));
  if (!source || !("operationId" in source || "groupingFactorId" in source)) return body;

  /** Parameter value source represented by the edited item. */
  const valueSource = "operationId" in source ? "operation" : "groupingFactor";
  /** Identifier property used to determine whether every group has the same source type. */
  const sourceKey = valueSource === "operation" ? "operationId" : "groupingFactorId";
  /** Groups on the same table axis as the edited item. */
  const tableGroups = body[section];
  /** Group that owns the edited item. */
  const owner = tableGroups[ownerIndex];

  /** Paths to matching parameter items, limited to sibling operations when syncing operations. */
  const targets = (
    tableGroups.every((group) => sourceKey in group)
      ? tableGroups.flatMap((group, index) =>
          collectParameterPaths(group, `/${section}/${index}`, valueSource),
        )
      : collectParameterPaths(owner, `/${section}/${ownerIndex}`, valueSource)
  ).filter(
    (targetPath) =>
      valueSource !== "operation" ||
      targetPath.slice(0, targetPath.lastIndexOf("/")) ===
        itemPath.slice(0, itemPath.lastIndexOf("/")),
  );
  /** Method parameters applicable to the edited item's value source. */
  const parameters =
    inheritedMethod.codeTemplate?.parameters?.filter(
      (parameter) => parameter.valueSource === valueSource,
    ) ?? [];
  /** Existing matching items used to seed values when the edited item was just inserted. */
  const peerItems = inserting
    ? targets
        .filter((targetPath) => targetPath !== itemPath)
        .map((targetPath) =>
          getFromDataPath(targetPath, body, (obj) => isArsTableGroupCalculationDto(obj, isT)),
        )
        .filter((target): target is NonNullable<typeof target> => target !== undefined)
    : [];
  /** Existing peer chosen as the value source for a newly inserted item, if applicable. */
  const sourceForSync = inserting
    ? (peerItems.find(
        (peer) =>
          parameters.some(
            (parameter) => peer.parameters?.[normalizeParameterName(parameter.name)] !== undefined,
          ) ||
          ("operationId" in peer && peer.targetVariable !== ""),
      ) ?? peerItems[0])
    : source;
  if (!sourceForSync) return body;

  /** Body copy updated as each matching parameter value is synchronised. */
  let result = body;
  /** Each applicable method parameter to synchronise. */
  for (const parameter of parameters) {
    /** Each item path receiving the current parameter value. */
    for (const targetPath of targets) {
      /** JSON Pointer to the current parameter value. */
      const parameterPath = Pointer.fromJSON(`${targetPath}/parameters`);
      parameterPath.push(normalizeParameterName(parameter.name));
      result = mutateByPath(
        result,
        parameterPath.toString(),
        sourceForSync.parameters?.[normalizeParameterName(parameter.name)],
      );
    }
  }
  /** Each item path receiving the operation's target variable. */
  if ("operationId" in sourceForSync) {
    /** Each target path whose operation target variable is synchronised. */
    for (const targetPath of targets) {
      result = mutateByPath(result, `${targetPath}/targetVariable`, sourceForSync.targetVariable);
    }
  }
  return result;
};
